import { queueMacrotask } from 'module/helpers/macrotask';
import { useTranslation } from 'module/hooks/use-translation';
import { Form } from 'module/ui/components/atoms/form';
import { isTiptapJsonEmpty } from 'module/ui/components/atoms/rich-text-editor/utils/common';
import { RichTextEditorInput } from 'module/ui/components/atoms/rich-text-editor-input/editor';
import { CustomSectionState } from 'api/custom-section/types';
import { TiptapJson } from 'api/page-template/types';
import { FC, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { fragmentJSONToEditorContent } from 'components/organisms/document-editor/utils/node-utils';
import { SharedConfigurationBar } from '../../../shared/configuration-bar';
import { SectionHeading } from '../../../shared/section-heading';
import {
  SectionCard,
  SectionContent,
  SectionDivider,
} from '../../../shared/section-styles';
import { TextBlockSectionComponentProps } from '../types';

const RTE = styled(RichTextEditorInput)`
  .rte-editor {
    --rte-min-height: 296px;
  }
`;

interface Props extends TextBlockSectionComponentProps {
  onEditDone: () => void;
}
type PromptInputFormValues = {
  content: TiptapJson;
};
export const TextBlockSectionPromptInput: FC<Props> = ({
  node,
  onRemove,
  onDone,
  onUndo,
  allowResolve,
  onEditDone,
  pageType,
  pageTriggerListFilter,
}) => {
  const requiredErrorMessage = useTranslation('input.required');
  const [hasError, setHasError] = useState(false);
  const [form] = Form.useForm<PromptInputFormValues>();
  const onValuesChange = useCallback(() => {
    queueMacrotask(() => {
      form
        .validateFields()
        .then(() => {
          setHasError(false);
        })
        .catch(() => {
          form.setFields([
            {
              name: 'content',
              value: form.getFieldValue('content'),
              errors: [],
            },
            {
              name: 'title',
              value: form.getFieldValue('title'),
              errors: [],
            },
          ]);
          setHasError(true);
        });
    });
  }, [form]);

  useEffect(() => {
    form.setFieldsValue({
      content: fragmentJSONToEditorContent(node.attrs.config.content),
    });
    onValuesChange();
  }, [form, node.attrs.config.content, node.attrs.title, onValuesChange]);
  const [actionLoading, setActionLoading] = useState(false);
  const onSubmit = useCallback(() => {
    form.validateFields().then((values) => {
      setActionLoading(true);
      const payload = {
        ...node.attrs,
        state: CustomSectionState.Closed,
        config: {
          content: values.content.content,
        },
      };

      onDone(payload).then(() => {
        setActionLoading(false);
        onEditDone();
      });
    });
  }, [form, node.attrs, onDone, onEditDone]);

  return (
    <SectionCard>
      <SectionHeading
        title="editor.sections.text_block_section.title"
        onClose={onUndo}
        showCloseButton={!hasError}
      />
      <SectionContent>
        <Form
          layout="vertical"
          form={form}
          onValuesChange={onValuesChange}
          validateTrigger={['onChange']}
        >
          <Form.Item
            name="content"
            label={
              <FormattedMessage id="custom_section.text_block.fields.content.label" />
            }
            rules={[
              {
                validator: (_, value) => {
                  if (isTiptapJsonEmpty(value)) {
                    return Promise.reject(new Error(requiredErrorMessage));
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <RTE
              placeholder={useTranslation(
                'custom_section.text_block.fields.content.placeholder',
              )}
            />
          </Form.Item>
        </Form>
        <SectionDivider />
        <SharedConfigurationBar
          prompt={{}}
          setPrompt={() => {}}
          isActionLoading={actionLoading}
          onCancel={onRemove}
          onSubmit={onSubmit}
          validation={{
            isDisabled: hasError,
          }}
          allowResolve={allowResolve}
          defaultValues={{}}
          options={{}}
          pageType={pageType}
          pageTriggerListFilter={pageTriggerListFilter}
        />
      </SectionContent>
    </SectionCard>
  );
};
